/**
 * Theme adapter for Laithwaites NZ site — injects real next/font families.
 */
import type { Theme } from '@emotion/react';
import { buildTheme } from 'common-ui';
import type { CommonThemeVars, ThemeCreateFunction } from 'tsconfig/types';
import { notoSerifFont, oswaldFont, robotoFont } from 'common-ui/src/theme/theme/sharedNextFont';
import {
  buildThemeVarsAPACLWNZ as buildThemeVarsAPACLWNZFoundation,
  buildThemeMixinsAPACLWNZ as buildThemeMixinsAPACLWNZFoundation,
  buildThemeWidgetStylesAPACLWNZ as buildThemeWidgetStylesAPACLWNZFoundation,
  buildThemeWidgetStylesAPACLWNZFromBase,
  buildThemeWidgetStylesAPACLWFromBase,
  buildCoreWidgets,
  buildProductCardWidgets,
  buildProductWidgets,
  buildSearchWidgets,
  buildMiniCartWidgets,
  buildCartWidgets,
  buildAccountWidgets,
  buildLandingpageWidgets,
} from './themeFoundation';

export const buildThemeVarsAPACLWNZ = (
  vars: Pick<Theme, CommonThemeVars>,
): ReturnType<typeof buildThemeVarsAPACLWNZFoundation> =>
  buildThemeVarsAPACLWNZFoundation(vars, {
    oswald: oswaldFont.style.fontFamily,
    roboto: robotoFont.style.fontFamily,
    noto: notoSerifFont.style.fontFamily,
  });

export const buildThemeMixinsAPACLWNZ = (
  vars: Omit<Theme, 'mixins' | 'widgets'>,
): ReturnType<typeof buildThemeMixinsAPACLWNZFoundation> => buildThemeMixinsAPACLWNZFoundation(vars);

export const buildThemeWidgetStylesAPACLWNZ = (
  vars: Omit<Theme, 'widgets'>,
): ReturnType<typeof buildThemeWidgetStylesAPACLWNZFoundation> => buildThemeWidgetStylesAPACLWNZFoundation(vars);

// CMS pages: core + product cards + minicart (header)
export const createCmsPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACLWNZ, buildThemeMixinsAPACLWNZ, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACLWNZFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductCardWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// PDP: core + full product + minicart (header)
export const createProductTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACLWNZ, buildThemeMixinsAPACLWNZ, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACLWNZFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Search/PLP: core + search + product cards + minicart
export const createSearchTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACLWNZ, buildThemeMixinsAPACLWNZ, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACLWNZFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildSearchWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Cart: core + cart + minicart
export const createCartTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACLWNZ, buildThemeMixinsAPACLWNZ, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACLWNZFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildCartWidgets(vars),
      }),
    ),
  );

// Account: core + account + minicart (header)
export const createAccountTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACLWNZ, buildThemeMixinsAPACLWNZ, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACLWNZFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildAccountWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Landing/offer pages: core + landing page + minicart
export const createLandingPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACLWNZ, buildThemeMixinsAPACLWNZ, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACLWNZFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildLandingpageWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

const createTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACLWNZ, buildThemeMixinsAPACLWNZ, buildThemeWidgetStylesAPACLWNZ);
export default createTheme;
